/**
 * NAV-6.2 — which Outlet Suspense fallback to paint, from a pathname.
 *
 * Pure so it can be unit-tested without a DOM. Commission stays the first
 * branch and keeps its own kinds; every other mapped /app destination gets a
 * reusable app kind. Unmapped leftovers return null so MainLayout keeps
 * SectionLoadingSpinner.
 */
import {
  commissionSkeletonKindForPath,
  type CommissionSkeletonKind,
} from '@/lib/commissionDestinationSkeleton';

export type AppOutletSkeletonKind =
  | 'dashboard'
  | 'chat'
  | 'earnings'
  | 'kanban'
  | 'settings'
  | 'table'
  | 'calendar'
  | 'form';

export type OutletSkeletonMatch =
  | { family: 'commission'; kind: CommissionSkeletonKind }
  | { family: 'app'; kind: AppOutletSkeletonKind };

const TABLE_PREFIXES = [
  '/app/reporting',
  '/app/contracts',
  '/app/tracking',
  '/app/support-tickets',
  '/app/support',
  '/app/notifications',
  '/app/tasks',
  '/app/redlines',
  '/app/commercial-redlines',
  '/app/ses-redlines',
] as const;

const FORM_PREFIXES = [
  '/app/profile',
  '/app/sub-accounts',
  '/app/new-project',
  '/app/add-user',
] as const;

const DASHBOARD_PREFIXES = [
  '/app/dashboards',
  '/app/dashboard',
  '/app/portfolio',
] as const;

const KANBAN_PREFIXES = [
  '/app/pipeline-resi',
  '/app/pipeline',
  '/app/onboarding',
] as const;

/** Strip a trailing slash so `/app/chat/` and `/app/chat` resolve the same. */
export function normalizeOutletPath(pathname: string): string {
  const withoutQuery = pathname.split('?')[0] ?? pathname;
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.replace(/\/+$/, '');
  }
  return withoutQuery;
}

function pathMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function outletSkeletonForPath(pathname: string): OutletSkeletonMatch | null {
  const path = normalizeOutletPath(pathname);

  const commission = commissionSkeletonKindForPath(path);
  if (commission) return { family: 'commission', kind: commission };

  if (DASHBOARD_PREFIXES.some((prefix) => pathMatches(path, prefix))) {
    return { family: 'app', kind: 'dashboard' };
  }
  if (pathMatches(path, '/app/chat')) {
    return { family: 'app', kind: 'chat' };
  }
  if (pathMatches(path, '/app/earnings')) {
    return { family: 'app', kind: 'earnings' };
  }
  if (KANBAN_PREFIXES.some((prefix) => pathMatches(path, prefix))) {
    return { family: 'app', kind: 'kanban' };
  }
  if (pathMatches(path, '/app/settings')) {
    return { family: 'app', kind: 'settings' };
  }
  if (TABLE_PREFIXES.some((prefix) => pathMatches(path, prefix))) {
    return { family: 'app', kind: 'table' };
  }
  // Booking-links / requests are a different page than Schedule's calendar.
  if (path.startsWith('/app/scheduler/booking-links')) {
    return null;
  }
  if (pathMatches(path, '/app/scheduler')) {
    return { family: 'app', kind: 'calendar' };
  }
  if (FORM_PREFIXES.some((prefix) => pathMatches(path, prefix))) {
    return { family: 'app', kind: 'form' };
  }
  return null;
}
